/**
 * Gives the cart ladder a memory, so the bar MOVES between two states rather
 * than resetting to empty and re-drawing every time the cart changes.
 *
 * The drawer re-renders by replacing .drawer__inner's innerHTML, which throws
 * away the old bar and its computed width — and never executes an inline
 * <script>. A custom element is the one thing that still runs on insertion, so
 * the previous fill is carried across renders on window and handed to the new
 * bar as --sb-ladder-from before its animation can start.
 *
 * Two cases, deliberately different:
 *   drawer closed  → from 0, so opening it plays the full draw
 *   drawer open    → from wherever the bar already stood, up OR down
 *
 * The memory is kept per surface. The cart page carries TWO ladders — its own
 * and the hidden drawer's — and the drawer's, upgraded first, used to hand the
 * page bar its own final width as the start, so the page bar never drew.
 *
 * It also times the stop pops. The draw is eased, so the moment the sweep
 * actually crosses a stop is the inverse of that curve — and it depends on
 * where the sweep starts, which only this element knows (the fill is
 * continuous below the shipping stop, so Liquid cannot tabulate it either).
 * Liquid writes a fallback for a draw from empty; this replaces it with the
 * exact delay, and marks the stops the sweep starts behind as settled so they
 * hold their state instead of popping again.
 */
if (!customElements.get('sb-cart-ladder')) {
  // Duration and timing function of sb-ladder-draw in sb-cart-ladder.liquid.
  // Change them together or the pops drift off the sweep.
  const SB_LADDER_DRAW_MS = 1100;
  const SB_LADDER_CURVE = [0.5, 0.15, 0.35, 1];

  customElements.define(
    'sb-cart-ladder',
    class SbCartLadder extends HTMLElement {
      connectedCallback() {
        const target = parseFloat(this.dataset.fill) || 0;
        const drawer = this.closest('cart-drawer');
        // No drawer means the cart page, where the bar is always on screen.
        const isLive = drawer ? drawer.classList.contains('active') : true;
        const memory = SbCartLadder.memory();
        const surface = drawer ? 'drawer' : 'page';
        const previous = memory[surface];
        const from = isLive && typeof previous === 'number' ? previous : 0;

        this.style.setProperty('--sb-ladder-from', from + '%');
        this.dataset.direction = target < from ? 'down' : 'up';
        memory[surface] = target;

        this.timeStops(from, target);
        this.restart();

        SbCartLadder.watchDrawer(drawer);
      }

      /**
       * Each stop (and its caption) lights when the sweep arrives at it: its
       * share of the sweep, pushed through the inverse of the draw's curve.
       * A stop the sweep never crosses — already behind it, still won — is
       * settled and does not animate at all.
       */
      timeStops(from, target) {
        const span = target - from;
        this.querySelectorAll('[data-at]').forEach((stop) => {
          const at = parseFloat(stop.dataset.at);
          let delay = 0;
          if (span > 0 && at > from) {
            const share = Math.min(1, (at - from) / span);
            delay = Math.round(SB_LADDER_DRAW_MS * SbCartLadder.timeAt(share));
          }
          stop.style.setProperty('--sb-stop-delay', delay + 'ms');
          if (from > 0 && at <= Math.min(from, target)) {
            stop.dataset.settled = '';
          } else {
            delete stop.dataset.settled;
          }
        });
      }

      /**
       * The fraction of the draw's duration at which the eased sweep has
       * covered `share` of its length. Bisects the curve's progress axis, then
       * reads the time axis at that point.
       */
      static timeAt(share) {
        if (share <= 0) return 0;
        if (share >= 1) return 1;
        const [x1, y1, x2, y2] = SB_LADDER_CURVE;
        const bezier = (a, b, t) => 3 * (1 - t) * (1 - t) * t * a + 3 * (1 - t) * t * t * b + t * t * t;
        let lo = 0;
        let hi = 1;
        for (let i = 0; i < 24; i += 1) {
          const mid = (lo + hi) / 2;
          if (bezier(y1, y2, mid) < share) lo = mid;
          else hi = mid;
        }
        return bezier(x1, x2, (lo + hi) / 2);
      }

      /**
       * Re-commit: the animations can otherwise already be running against the
       * default from-value and the fallback delays by the time the properties
       * land. Every animated node restarts, not just the fill, because each
       * stop's delay is its own.
       */
      restart() {
        const nodes = this.querySelectorAll(
          '.sb-ladder__fill, .sb-ladder__pill, .sb-ladder__dot, .sb-ladder__cap, .sb-ladder__prompt'
        );
        nodes.forEach((node) => {
          node.style.animation = 'none';
        });
        const band = this.querySelector('.sb-ladder');
        if (band) void band.offsetWidth;
        nodes.forEach((node) => {
          node.style.animation = '';
        });
      }

      /** Previous fill per surface, carried across re-renders on window. */
      static memory() {
        if (!window.sbCartLadderFill || typeof window.sbCartLadderFill !== 'object') {
          window.sbCartLadderFill = {};
        }
        return window.sbCartLadderFill;
      }

      /**
       * Closing the drawer rearms the full draw. Without this, a shopper who
       * removed an item, closed the drawer and reopened it would watch the bar
       * play that decrease again instead of simply filling to where it stands.
       */
      static watchDrawer(drawer) {
        if (!drawer || window.sbCartLadderWatching) return;
        window.sbCartLadderWatching = true;

        new MutationObserver(() => {
          if (drawer.classList.contains('active')) return;
          SbCartLadder.memory().drawer = undefined;
          drawer.querySelectorAll('sb-cart-ladder').forEach((ladder) => {
            // The bar that is still in the drawer may have last moved DOWN,
            // or from part-way: reopening must be a plain draw from empty.
            ladder.style.setProperty('--sb-ladder-from', '0%');
            ladder.dataset.direction = 'up';
            ladder.timeStops(0, parseFloat(ladder.dataset.fill) || 0);
          });
        }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }
    }
  );
}
